import { z } from 'zod';
import { kebabCase, mapKeys, mapValues } from 'lodash';
import type { CmsPageCollectionItems } from '$cms/types';
import { CmsLinkSchema, CmsShortTextSchema } from '$cms/types';
import type { SchemaType } from '$util/schema';
import type { ContentComponent } from '$cms/ContentComponent';
import { useCmsPageContext } from '$cms/CmsPageContext';
import { extendCmsText } from '$cms/index';
import { useAppContext } from '$util/AppContext';
import { getSegmentFromLocale } from '$util/getSegment';
import { CarMakesList } from '@/components/molecules/CarMakesList';

const defaultConfiguration = {
  showAllMakes: false,
};

const CmsCarMakesListSchema = {
  translatedEntities: z.object({
    title: CmsShortTextSchema,
    viewAllLink: CmsLinkSchema,
  }),
  configuration: z
    .object({
      showAllMakes: z.boolean().optional(),
    })
    .default(defaultConfiguration),
};

export type CmsCarMakesListTranslatedEntities = SchemaType<
  (typeof CmsCarMakesListSchema)['translatedEntities']
>;
export type CmsCarMakesListConfiguration = SchemaType<(typeof CmsCarMakesListSchema)['configuration']>;

const makePlaceholders = (collectionItems: CmsPageCollectionItems): Record<string, string | number> =>
  mapValues(
    mapKeys(collectionItems, (value, key) => kebabCase(key)),
    (value) => value?.name || ''
  );

export const CmsCarMakesList: ContentComponent<
  CmsCarMakesListTranslatedEntities,
  CmsCarMakesListConfiguration
> = function CmsCarMakesList({ translatedEntities, configuration }): JSX.Element {
  const cmsPageContext = useCmsPageContext();
  const { locale } = useAppContext();
  const segment = getSegmentFromLocale(locale);
  const collectionItems = cmsPageContext?.pageRoutes?.[0]?.collectionItems;

  const { title, viewAllLink } = translatedEntities;
  const showAllMakes = configuration?.showAllMakes;

  return (
    <CarMakesList
      title={collectionItems ? extendCmsText(title.value, makePlaceholders(collectionItems)) : title.value}
      viewAllLinkLabel={viewAllLink.text}
      viewAllLinkUrl={viewAllLink.url}
      showAllMakes={showAllMakes}
      segment={segment}
    />
  );
};

CmsCarMakesList.displayName = 'CarMakesList';
CmsCarMakesList.schema = CmsCarMakesListSchema;
